import type {
  LogResponse,
  MagoDeviceResponse,
  DeviceStatusResponse,
  StatsResponse,
  HealthResponse,
  PublishRequest,
  PublishResponse,
  EndpointsResponse,
  CommandType,
  SensorAlertResponse,
  AlertSummaryResponse,
  AlertAckRequest,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  UserResponse,
  UserCreateRequest,
  UserUpdateRequest,
  UserDetailResponse,
  UserListResponse,
  PermissionCreateRequest,
  PermissionDetailResponse,
  UserPermissionsResponse,
  QrGenerateResponse,
} from '../types/api'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000'

class ApiClient {
  private onUnauthorized?: () => void

  setUnauthorizedHandler(handler: () => void) {
    this.onUnauthorized = handler
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Add cache busting for critical endpoints
    const isCriticalEndpoint = endpoint.includes('/api/device-status') || endpoint.includes('/api/mago')
    const finalEndpoint = isCriticalEndpoint
      ? `${endpoint}${endpoint.includes('?') ? '&' : '?'}_t=${Date.now()}`
      : endpoint

    const response = await fetch(`${API_BASE}${finalEndpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(isCriticalEndpoint && {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }),
        ...options.headers,
      },
      credentials: 'include', // Include cookies for session-based auth
      ...(isCriticalEndpoint && { cache: 'no-cache' }),
      ...options,
    })

    if (!response.ok) {
      const error = new Error(`API Error: ${response.status} ${response.statusText}`)
      ;(error as any).status = response.status

      // Handle 401 Unauthorized - trigger auto-logout
      if (response.status === 401 && this.onUnauthorized) {
        this.onUnauthorized()
      }

      throw error
    }

    return response.json()
  }

  async getHealth(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/health')
  }

  async getStats(): Promise<StatsResponse> {
    return this.request<StatsResponse>('/api/stats')
  }

  async getLogs(
    params: { mago_id?: string; endpoint?: string; limit?: number; offset?: number } = {}
  ): Promise<LogResponse[]> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString())
      }
    })

    const query = searchParams.toString()
    return this.request<LogResponse[]>(`/api/logs${query ? `?${query}` : ''}`)
  }

  async getLatestLog(magoId: string): Promise<LogResponse> {
    return this.request<LogResponse>(`/api/logs/${magoId}/latest`)
  }

  async getCommandLogs(
    command: CommandType,
    params: { limit?: number; offset?: number } = {}
  ): Promise<LogResponse[]> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString())
      }
    })

    const query = searchParams.toString()
    return this.request<LogResponse[]>(`/api/commands/${command}${query ? `?${query}` : ''}`)
  }

  async getErrors(params: { hours?: number; limit?: number } = {}): Promise<LogResponse[]> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString())
      }
    })

    const query = searchParams.toString()
    return this.request<LogResponse[]>(`/api/errors${query ? `?${query}` : ''}`)
  }

  async getDevices(
    params: { group_id?: string; status?: string; limit?: number; offset?: number } = {}
  ): Promise<MagoDeviceResponse[]> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString())
      }
    })

    const query = searchParams.toString()
    return this.request<MagoDeviceResponse[]>(`/api/devices${query ? `?${query}` : ''}`)
  }

  async getDeviceStatus(
    params: { endpoint?: string; limit?: number; offset?: number } = {}
  ): Promise<DeviceStatusResponse[]> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString())
      }
    })

    const query = searchParams.toString()
    return this.request<DeviceStatusResponse[]>(`/api/device-status${query ? `?${query}` : ''}`)
  }

  async publishMessage(request: PublishRequest): Promise<PublishResponse> {
    return this.request<PublishResponse>('/api/publish', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async getEndpoints(): Promise<EndpointsResponse> {
    return this.request<EndpointsResponse>('/api/endpoints')
  }

  // Sensor Alert APIs
  async getSensorAlerts(
    params: { 
      mago_id?: string; 
      alert_level?: 'warning' | 'critical'; 
      status?: 'active' | 'resolved' | 'acknowledged' | 'all';
      limit?: number; 
      offset?: number 
    } = {}
  ): Promise<SensorAlertResponse[]> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString())
      }
    })

    const query = searchParams.toString()
    return this.request<SensorAlertResponse[]>(`/api/sensor-alerts${query ? `?${query}` : ''}`)
  }

  async getAlertSummary(): Promise<AlertSummaryResponse> {
    return this.request<AlertSummaryResponse>('/api/sensor-alerts/summary')
  }

  async getDeviceAlerts(mago_id: string): Promise<Record<string, SensorAlertResponse | null>> {
    return this.request<Record<string, SensorAlertResponse | null>>(`/api/sensor-alerts/${mago_id}`)
  }

  async acknowledgeAlert(alert_id: number, request: AlertAckRequest = {}): Promise<{ status: string; message: string }> {
    return this.request<{ status: string; message: string }>(`/api/sensor-alerts/${alert_id}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async acknowledgeDeviceAlerts(mago_id: string, request: AlertAckRequest = {}): Promise<{ status: string; message: string; acknowledged_count: number }> {
    return this.request<{ status: string; message: string; acknowledged_count: number }>(`/api/sensor-alerts/device/${mago_id}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async resolveAlert(alert_id: number): Promise<{ status: string; message: string }> {
    return this.request<{ status: string; message: string }>(`/api/sensor-alerts/${alert_id}/resolve`, {
      method: 'POST',
    })
  }

  // Authentication APIs
  async login(username: string, password: string): Promise<LoginResponse> {
    return this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password } as LoginRequest),
    })
  }

  async logout(): Promise<LogoutResponse> {
    return this.request<LogoutResponse>('/api/auth/logout', {
      method: 'POST',
    })
  }

  async getCurrentUser(): Promise<UserResponse> {
    return this.request<UserResponse>('/api/auth/me')
  }

  // User Management APIs
  async getUsers(params: { limit?: number; offset?: number } = {}): Promise<UserListResponse> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString())
      }
    })

    const query = searchParams.toString()
    return this.request<UserListResponse>(`/api/users${query ? `?${query}` : ''}`)
  }

  async getUser(userId: number): Promise<UserDetailResponse> {
    return this.request<UserDetailResponse>(`/api/users/${userId}`)
  }

  async createUser(request: UserCreateRequest): Promise<UserDetailResponse> {
    return this.request<UserDetailResponse>('/api/users', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async updateUser(userId: number, request: UserUpdateRequest): Promise<UserDetailResponse> {
    return this.request<UserDetailResponse>(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    })
  }

  async deleteUser(userId: number): Promise<{ status: string; message: string }> {
    return this.request<{ status: string; message: string }>(`/api/users/${userId}`, {
      method: 'DELETE',
    })
  }

  // User Permissions APIs
  async getUserPermissions(userId: number): Promise<UserPermissionsResponse> {
    return this.request<UserPermissionsResponse>(`/api/users/${userId}/permissions`)
  }

  async addUserPermission(
    userId: number,
    request: PermissionCreateRequest
  ): Promise<PermissionDetailResponse> {
    return this.request<PermissionDetailResponse>(`/api/users/${userId}/permissions`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async deleteUserPermission(userId: number, permissionId: number): Promise<{ status: string; message: string }> {
    return this.request<{ status: string; message: string }>(`/api/users/${userId}/permissions/${permissionId}`, {
      method: 'DELETE',
    })
  }

  // QR Code APIs
  async generateQrCode(magoId: string, brand?: string): Promise<QrGenerateResponse> {
    const params = brand ? `?brand=${encodeURIComponent(brand)}` : ''
    return this.request<QrGenerateResponse>(`/api/qr/${encodeURIComponent(magoId)}${params}`)
  }

  // Master Data APIs
  async getDestinations(params: { limit?: number; offset?: number } = {}): Promise<any[]> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString())
      }
    })

    const query = searchParams.toString()
    return this.request<any[]>(`/api/dest${query ? `?${query}` : ''}`)
  }

  async getSubDestinations(
    params: { dest_id?: number; limit?: number; offset?: number } = {}
  ): Promise<any[]> {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString())
      }
    })

    const query = searchParams.toString()
    return this.request<any[]>(`/api/sub-dest${query ? `?${query}` : ''}`)
  }
}

export const apiClient = new ApiClient()
export default apiClient
